import React, { useEffect, useRef, useState } from "react";
import { COMMON_WEB_PATH } from "../../constant";
import { useDispatch, useSelector } from "react-redux";
import { toggleBGM } from "../../redux/action/action";
import useDeviceType from "../../hook/useDeviceType";
import usePwaDetected from "../../hook/usePwaDetected";
import { saveNotificationSubscription } from "../../api/postApi";

const AudioPlayer = () => {
  const audioRef = useRef(null);

  const isBGMEnabled = useSelector((state) => state.isBGMEnabled);
  const { enabled, song, musicVolume = 0.2 } = isBGMEnabled || {};

  const dispatch = useDispatch();

  const deviceType = useDeviceType();
  const isPwa = usePwaDetected();

  const [userInteracted, setUserInteracted] = useState(false);

  // 處理音樂播放/暫停
  const handlePlayback = async (determine) => {
    if (audioRef.current && userInteracted) {
      audioRef.current.muted = false;

      // 檢查音量是否為 0 或靜音狀態
      const shouldPlay = determine && !(musicVolume === 0);

      if (shouldPlay) {
        audioRef.current.loop = true;
        try {
          await audioRef.current.play();
        } catch (error) {
          console.error("Error in playback:", error);
        }
      } else {
        audioRef.current.pause();
      }
    }
  };

  // 單獨監聽音量變化
  useEffect(() => {
    if (audioRef.current) {
      // 設置音量
      audioRef.current.volume = musicVolume;

      // 如果音量為 0，確保暫停播放
      if (musicVolume === 0 && !audioRef.current.paused) {
        audioRef.current.pause();
      }
      // 如果音量大於 0 且啟用了音樂，且當前暫停中，則嘗試播放
      else if (
        musicVolume > 0 &&
        enabled &&
        userInteracted &&
        audioRef.current.paused
      ) {
        audioRef.current.play().catch((err) => console.error("播放失敗:", err));
      }
    }
  }, [musicVolume, enabled, userInteracted]);

  // 用瀏覽器的 Page Visibility API監聽頁面可見性變化 檢測頁面是否在用戶的視窗中可見
  // 當頁面被隱藏時暫停音樂，當頁面顯示時自動播放（前提是使用者已互動且背景音樂啟用）
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // 當頁面隱藏時，若有音樂在播放則暫停
        if (audioRef.current) {
          audioRef.current.pause(); // 暫停音樂播放
        }
      } else {
        // 當頁面再次顯示時，若使用者已互動且背景音樂啟用，則自動播放音樂
        if (audioRef.current && userInteracted && enabled && musicVolume > 0) {
          audioRef.current
            .play() // 播放音樂
            .catch((err) => console.error("自動播放失敗", err));
        }
      }
    };

    // visibilitychange 監聽頁面是否隱藏
    document.addEventListener("visibilitychange", handleVisibilityChange);
    // 移除監聽器(清理函數)
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [audioRef, enabled, userInteracted, musicVolume]);

  // 初始化音樂
  useEffect(() => {
    // 如果音樂已經存在，則暫停並重新創建
    if (audioRef.current) {
      const isPlaying = !audioRef.current.paused;
      audioRef.current.pause();

      // 創建新的音樂
      audioRef.current = new Audio(
        `${COMMON_WEB_PATH}/FlyDragon/${song || "back2"}.mp3?abc`
      );
      audioRef.current.muted = true;
      audioRef.current.loop = true;
      audioRef.current.volume = musicVolume;

      // 如果之前在播放且啟用了背景音樂且音量大於0，則繼續播放
      if (isPlaying && enabled && musicVolume > 0 && userInteracted) {
        audioRef.current.muted = false;
        audioRef.current.play().catch((e) => console.error("播放失敗:", e));
      }
    } else {
      // 第一次初始化
      audioRef.current = new Audio(
        `${COMMON_WEB_PATH}/FlyDragon/${song || "back2"}.mp3?abc`
      );
      audioRef.current.muted = true;
      audioRef.current.loop = true;
      audioRef.current.volume = musicVolume;
    }

    const handleFirstInteraction = () => {
      setUserInteracted(true);
    };

    document.addEventListener("click", handleFirstInteraction, { once: true });
    document.addEventListener("keydown", handleFirstInteraction, {
      once: true,
    });

    return () => {
      document.removeEventListener("click", handleFirstInteraction);
      document.removeEventListener("keydown", handleFirstInteraction);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, [song]); //改成只有 song 改變時才重新初始化音樂 拿掉musicVolume是因為音量改變時不用重新初始化音樂

  // 響應播放狀態變化
  useEffect(() => {
    // if (deviceType == "PC") {
    //   handlePlayback(enabled);
    // }
    // 只有在音量大於 0 時才執行播放/暫停操作
    handlePlayback(enabled && musicVolume > 0);
  }, [enabled, userInteracted, song, deviceType, musicVolume]);

  useEffect(() => {
    if (userInteracted && (isPwa || deviceType == "PC")) {
      Notification.requestPermission().then(function (result) {
        if (result === "granted") {
          navigator.serviceWorker.ready.then((sw) => {
            sw.pushManager
              .subscribe({
                userVisibleOnly: true,
                applicationServerKey:
                  process.env.REACT_APP_PWA_PUSH_NOTIFICATION_PUBLIC_KEY,
              })
              .then((subscription) => {
                //將訂閱內容發送API給server端儲存至DB
                saveNotificationSubscription({
                  subscription: subscription,
                });
              });
          });
        }
      });
    }
  }, [userInteracted, deviceType, isPwa]);

  return null;
};

export default AudioPlayer;
